import { Unsubscribe } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useAppSelector } from '@core/hooks/appHooks';
import { NetworkService } from '@core/services/networkService/networkService';
import {
  RoutingViews,
  SearchParams,
  routingPaths,
} from '@core/services/routingService';
import { selectVendor } from '@core/store/appSlice/appSlice';
import DemmiButton from '@demmi-ui/Button';
import DemmiCard from '@demmi-ui/Card/Card';
import { navigateTransitioned } from '@helpers/app.helper';
import { DemmiSupportHelper } from '@helpers/demmiSupport.helper';
import { DemmiFS } from '@subhanhabib/demmilib';

import OrderItemCollectionInfo from './partials/_collection-info';
import OrderItemHeader from './partials/_header';
import OrderItemTabCustomer from './tabs/_tab-customer';
import OrderItemTabProducts from './tabs/_tab-products';
import OrderItemTabRefund from './tabs/_tab-refund/_tab-refund';
import OrderItemTabReview from './tabs/_tab-review';
import OrderItemTabTimeline from './tabs/_tab-timeline';

export enum OrdersPageTabs {
  TIMELINE = 'Timeline',
  PRODUCTS = 'Products',
  CUSTOMER_DETAILS = 'Customer Details',
  REVIEW = 'Review',
  REFUND_STATUS = 'Refund Status',
}

interface Props {
  orderDocID: string;
}

const OrderItem: React.FC<Props> = ({ orderDocID }) => {
  const CSSBlock = 'order-item';
  const vendor = useAppSelector(selectVendor);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState<DemmiFS.Order>();
  const [orderReview, setOrderReview] = useState<DemmiFS.VendorReview>();
  const [status, setStatus] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isCreatingSupportTicket, setIsCreatingSupportTicket] = useState(false);

  const tabTitles = [
    'Timeline',
    'Products',
    'Customer Details',
    'Review',
    'Refund Status',
    // 'Delivery Information',
    // 'Customer Information',
  ];
  const tabContents = [
    <OrderItemTabTimeline
      CSSBlock={CSSBlock}
      order={order!}
      vendorID={vendor!.docID}
      key={0}
    />,
    <OrderItemTabProducts CSSBlock={CSSBlock} order={order!} key={1} />,
    <OrderItemTabCustomer CSSBlock={CSSBlock} order={order!} key={2} />,
    <OrderItemTabReview
      CSSBlock={CSSBlock}
      review={orderReview}
      vendorID={vendor!.docID}
      key={3}
    />,
    <OrderItemTabRefund order={order!} key={4} />,
  ];

  const orderCallback = (vendor: DemmiFS.Vendor) => (order?: DemmiFS.Order) => {
    setOrder(order);
    if (order)
      setStatus(
        DemmiFS.OrderStatusHelper.getStatusFromOrder(vendor.docID, order),
      );
  };
  const reviewCallback = (r?: DemmiFS.VendorReview) => setOrderReview(r);

  useEffect(() => {
    const snapshotListeners: Unsubscribe[] = [];
    if (vendor) {
      NetworkService.Orders.listenToOrder(
        orderDocID,
        vendor.docID,
        orderCallback(vendor),
      ).then(u => snapshotListeners.push(u));
      NetworkService.Orders.listenToOrderReview(
        orderDocID,
        vendor.docID,
        reviewCallback,
      ).then(u => {
        if (u) snapshotListeners.push(u);
      });
    }
    return () => snapshotListeners.forEach(snapshot => snapshot());
  }, [vendor, orderDocID]);

  useEffect(() => {
    if (searchParams.get(SearchParams.ACTIVE_TAB)) {
      const tabIndex = Object.values(OrdersPageTabs).findIndex(
        tab => tab === searchParams.get(SearchParams.ACTIVE_TAB),
      );
      if (tabIndex > -1) setActiveTab(tabIndex);
    } else {
      setActiveTab(0);
    }
  }, [searchParams]);

  // useEffect(() => {
  //   if (order && vendor) {
  //     setIsCheckingRefundRequest(true);
  //     demmiRequest(
  //       NetworkService.RefundRequests.hasRefundRequest(
  //         order.docID,
  //         vendor.docID,
  //       ),
  //     ).then(o => {
  //       setRefundRequestID(o);
  //       setIsCheckingRefundRequest(false);
  //     });
  //   } else {
  //     setRefundRequestID(undefined);
  //   }
  // }, [order, vendor]);

  const onClickTab = (i: number) => {
    searchParams.set(SearchParams.ACTIVE_TAB, Object.values(OrdersPageTabs)[i]);
    setSearchParams(searchParams);
    setActiveTab(i);
  };

  const openSupportTicket = async () => {
    if (!vendor) return;
    setIsCreatingSupportTicket(true);
    await DemmiSupportHelper.createSupportTicket(
      DemmiSupportHelper.DemmiSupportTypes.ORDER_GENERIC,
      vendor,
      (success, chatID) => {
        if (!success) {
          setIsCreatingSupportTicket(false);
        }
        navigateTransitioned({
          navigate,
          to: routingPaths[RoutingViews.DEMMI_SUPPORT],
          search: {
            [SearchParams.SUPPORT_CHAT_ID]: chatID!,
          },
        });
        setIsCreatingSupportTicket(false);
      },
      orderDocID,
    );
  };

  return (
    <div className={`${CSSBlock}__wrapper`}>
      {order && (
        <>
          <div className={CSSBlock}>
            <OrderItemHeader order={order} status={status} />
            <OrderItemCollectionInfo order={order} />
            {/* <OrderItemCosting
              CSSBlock={CSSBlock}
              order={order}
              status={status}
            /> */}
            <DemmiButton
              disabled={isCreatingSupportTicket}
              text={'Open Support Ticket'}
              faIcon="fa-message-bot"
              onClick={openSupportTicket}
            />
            <DemmiCard
              activeTab={activeTab}
              tabs={tabTitles}
              onClickTab={onClickTab}
              tabContents={tabContents}
              className={`${CSSBlock}__details`}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default OrderItem;
